import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useDestekBildirim } from '../contexts/DestekBildirimContext';
import TodoPanel from '../components/TodoPanel';
import ProjeGorunumleri from '../components/ProjeGorunumleri';
import axiosInstance from '../utils/axiosConfig';
import 'admin-lte/dist/css/adminlte.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'jquery/dist/jquery.min.js';
import 'admin-lte/dist/js/adminlte.min.js';
import './DashboardLayout.css';

const DashboardLayout: React.FC = () => {
    const { user, logout, hasPermission } = useAuth();
    const { bildirimSayisi } = useDestekBildirim();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isDefinitionsOpen, setIsDefinitionsOpen] = React.useState(false);
    const [isFinansalOpen, setIsFinansalOpen] = React.useState(false);
    const [isAraclarOpen, setIsAraclarOpen] = React.useState(false);
    const [isAyarlarOpen, setIsAyarlarOpen] = React.useState(false);
    const [isKiralikOpen, setIsKiralikOpen] = React.useState(false);
    const [isEmlakOpen, setIsEmlakOpen] = React.useState(false);
    const [isProjelerOpen, setIsProjelerOpen] = React.useState(false);
    const [showGorunumlerModal, setShowGorunumlerModal] = React.useState(false);
    const [gorunumlerStep, setGorunumlerStep] = React.useState<'proje' | 'gorunum'>('proje');
    const [gorunumlerProjeler, setGorunumlerProjeler] = React.useState<any[]>([]);
    const [selectedGorunumProje, setSelectedGorunumProje] = React.useState<any | null>(null);
    const [gorunumlerDaireler, setGorunumlerDaireler] = React.useState<any[]>([]);
    const [gorunumlerBloklar, setGorunumlerBloklar] = React.useState<any[]>([]);
    const [gorunumlerKatlar, setGorunumlerKatlar] = React.useState<any[]>([]);
    const [gorunumlerCepheler, setGorunumlerCepheler] = React.useState<any[]>([]);
    const [gorunumlerManzaralar, setGorunumlerManzaralar] = React.useState<any[]>([]);
    const [gorunumlerLoading, setGorunumlerLoading] = React.useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [showTodo, setShowTodo] = useState(false);
    const [todoActiveCount, setTodoActiveCount] = useState(0);
    const [showBildirimPanel, setShowBildirimPanel] = useState(false);
    const [bildirimler, setBildirimler] = useState<any[]>([]);
    const [okunmamisSayisi, setOkunmamisSayisi] = useState(0);
    const bildirimPanelRef = useRef<HTMLDivElement>(null);
    const [showHesapMakinesi, setShowHesapMakinesi] = useState(false);
    // Hesap makinesi state — modal kapansa bile hafızda kalır
    const [calcDisplay, setCalcDisplay] = useState('0');
    const [calcPrev, setCalcPrev] = useState<string | null>(null);
    const [calcOp, setCalcOp] = useState<string | null>(null);
    const [calcNewNum, setCalcNewNum] = useState(true);
    // Hesap makinesi sürükleme
    const [calcPos, setCalcPos] = useState<{ x: number; y: number } | null>(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    // Drag event listeners
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            setCalcPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
        };
        const onUp = () => { isDragging.current = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);

    const [firmaAdi, setFirmaAdi] = useState<string>('YapiCRM');
    const [currencies, setCurrencies] = useState<{ usd: string; eur: string }>({ usd: '--', eur: '--' });
    const [showDovizModal, setShowDovizModal] = useState(false);

    // Aktif sayfayı kontrol eden yardımcı fonksiyon
    const isActive = (path: string) => {
        return location.pathname === path;
    };

    // Tanımlamalar menüsünün açık olup olmadığını kontrol et
    useEffect(() => {
        if (location.pathname.startsWith('/tanimlamalar')) {
            setIsDefinitionsOpen(true);
        }
        if (location.pathname.startsWith('/kasa-hareketleri') || location.pathname.startsWith('/finansal-islemler') || location.pathname.startsWith('/raporlar') || location.pathname.startsWith('/hesap-hareketleri-raporu') || location.pathname.startsWith('/taksit-takip')) {
            setIsFinansalOpen(true);
        }
        if (location.pathname.startsWith('/fihrist') || location.pathname.startsWith('/kredi-hesaplama') || location.pathname.startsWith('/dokumanlar')) {
            setIsAraclarOpen(true);
        }
        if (location.pathname.startsWith('/ayarlar')) {
            setIsAyarlarOpen(true);
        }
        if (location.pathname.startsWith('/kiralik')) {
            setIsKiralikOpen(true);
        }
        if (location.pathname.startsWith('/emlak-portfoy') || location.pathname.startsWith('/emlak-sunumlari') || location.pathname.startsWith('/emlak-rapor')) {
            setIsEmlakOpen(true);
        }
        if (location.pathname.startsWith('/daireler') || location.pathname.startsWith('/sozlesmeler') || location.pathname.startsWith('/satis-sozlesmesi') || location.pathname.startsWith('/musteri-sunumlari') || location.pathname === '/ozet' || location.pathname === '/proje-raporlari') {
            setIsProjelerOpen(true);
        }
    }, [location]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
        // Debug için user bilgilerini konsola yazdıralım
        console.log('Current User:', user);
        console.log('LocalStorage User:', localStorage.getItem('user'));
        
        // Kurumsal bilgileri ve döviz kurlarını yükle
        fetchKurumsalBilgiler();
        fetchCurrencies();
    }, [user, navigate]);

    const openGorunumlerModal = async () => {
        try {
            const res = await axiosInstance.get('/api/Projeler');
            setGorunumlerProjeler(res.data || []);
        } catch {}
        setGorunumlerStep('proje');
        setSelectedGorunumProje(null);
        setShowGorunumlerModal(true);
    };

    const selectGorunumProje = async (proje: any) => {
        setGorunumlerLoading(true);
        try {
            const [dRes, bRes, kRes, cRes, mRes] = await Promise.all([
                axiosInstance.get(`/api/Daireler?projeID=${proje.projeID}&hepsi=true`),
                axiosInstance.get(`/api/Bloklar/proje/${proje.projeID}`),
                axiosInstance.get('/api/Katlar'),
                axiosInstance.get('/api/Cepheler'),
                axiosInstance.get('/api/Manzaralar'),
            ]);
            setGorunumlerDaireler(dRes.data?.items || dRes.data || []);
            setGorunumlerBloklar(bRes.data || []);
            setGorunumlerKatlar(kRes.data || []);
            setGorunumlerCepheler(cRes.data || []);
            setGorunumlerManzaralar(mRes.data || []);
            setSelectedGorunumProje(proje);
            setGorunumlerStep('gorunum');
        } catch {}
        setGorunumlerLoading(false);
    };

    const fetchOkunmamisSayisi = async () => {
        try {
            const res = await axiosInstance.get('/api/Bildirimler/okunmamis-sayisi');
            setOkunmamisSayisi(res.data.sayi ?? 0);
        } catch {}
    };

    const fetchBildirimler = async () => {
        try {
            const res = await axiosInstance.get('/api/Bildirimler/benim');
            setBildirimler(res.data || []);
        } catch {}
    };

    const handleBildirimIconClick = async () => {
        if (!showBildirimPanel) {
            await fetchBildirimler();
        }
        setShowBildirimPanel(v => !v);
    };

    const handleBildirimOku = async (id: number) => {
        try {
            await axiosInstance.post(`/api/Bildirimler/oku/${id}`);
            setBildirimler(prev => prev.map(b => b.bildirimID === id ? { ...b, durum: 'Okundu' } : b));
            setOkunmamisSayisi(prev => Math.max(0, prev - 1));
        } catch {}
    };

    const handleTumunuOku = async () => {
        try {
            await axiosInstance.post('/api/Bildirimler/tumunu-oku');
            setBildirimler(prev => prev.map(b => ({ ...b, durum: 'Okundu' })));
            setOkunmamisSayisi(0);
        } catch {}
    };

    const handleBildirimSil = async (id: number) => {
        try {
            await axiosInstance.post(`/api/Bildirimler/delete/${id}`);
            const silinen = bildirimler.find(b => b.bildirimID === id);
            setBildirimler(prev => prev.filter(b => b.bildirimID !== id));
            if (silinen && silinen.durum === 'Bekliyor') setOkunmamisSayisi(prev => Math.max(0, prev - 1));
        } catch {}
    };

    useEffect(() => {
        if (!user) return;
        fetchOkunmamisSayisi();
        const interval = setInterval(fetchOkunmamisSayisi, 60000);
        return () => clearInterval(interval);
    }, [user]);

    useEffect(() => {
        if (!showBildirimPanel) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (bildirimPanelRef.current && !bildirimPanelRef.current.contains(e.target as Node)) {
                setShowBildirimPanel(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showBildirimPanel]);

    const fetchKurumsalBilgiler = async () => {
        try {
            const response = await axiosInstance.get('/api/Kurumsal/1');
            if (response.data && response.data.firmaAdi) {
                setFirmaAdi(response.data.firmaAdi);
            }
        } catch (error) {
            console.log('Kurumsal bilgiler yüklenemedi, varsayılan isim kullanılıyor:', error);
        }
    };

    const fetchCurrencies = async () => {
        try {
            const res = await axiosInstance.get('/api/DovizKuru');
            if (res.data && res.data.kurlar) {
                const kurlar = res.data.kurlar;
                const usdRate = kurlar.find((r: any) => r.kod === 'USD');
                const eurRate = kurlar.find((r: any) => r.kod === 'EUR');
                setCurrencies({
                    usd: usdRate ? parseFloat(usdRate.satis).toFixed(2) : '--',
                    eur: eurRate ? parseFloat(eurRate.satis).toFixed(2) : '--',
                });
            }
        } catch {
            // Döviz kurları yüklenemedi, sessizce devam et
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    const openMobileSidebar = () => {
        document.body.classList.add('sidebar-open');
        document.body.classList.remove('sidebar-closed');
        setIsMobileNavOpen(true);
    };

    const closeMobileSidebar = () => {
        document.body.classList.remove('sidebar-open');
        document.body.classList.add('sidebar-closed');
        setIsMobileNavOpen(false);
    };

    const toggleMobileSidebar = () => {
        if (isMobileNavOpen) {
            closeMobileSidebar();
        } else {
            openMobileSidebar();
        }
    };

    // Sayfa değiştiğinde mobil sidebar'ı kapat
    useEffect(() => {
        closeMobileSidebar();
    }, [location.pathname]);

    if (!user) {
        return null;
    }

    return (
        <div className="wrapper">
            {/* Navbar */}
            <nav className="main-header navbar navbar-expand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {/* Sol: hamburger */}
                <ul className="navbar-nav" style={{ display: 'flex', alignItems: 'center' }}>
                    <li className="nav-item">
                        <a className="nav-link" href="#" role="button" style={{ color: '#6b7280', fontSize: '1rem' }}
                           onClick={(e) => { e.preventDefault(); toggleMobileSidebar(); }}>
                            <i className="fas fa-bars"></i>
                        </a>
                    </li>
                </ul>

                {/* Sağ: döviz + profil dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    {/* Döviz kurları — masaüstü */}
                    <div className="topbar-currency">
                        <span className="currency-badge">
                            <span className="currency-label">USD</span>
                            {currencies.usd}
                        </span>
                        <span className="currency-badge">
                            <span className="currency-label">EUR</span>
                            {currencies.eur}
                        </span>
                    </div>

                    {/* Döviz kuru butonu — sadece mobilde görünür */}
                    <button
                        className="topbar-doviz-btn d-lg-none"
                        title="Döviz Kurları"
                        onClick={() => setShowDovizModal(true)}
                    >
                        <i className="fas fa-dollar-sign"></i>
                    </button>

                    {/* Görev (To-Do) butonu */}
                    <button
                        title="Görevlerim"
                        onClick={() => setShowTodo(v => !v)}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#6b7280', fontSize: 17, padding: '4px 8px',
                            borderRadius: 8, lineHeight: 1, position: 'relative',
                            transition: 'color 0.15s',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
                    >
                        <i className="fas fa-tasks"></i>
                        {todoActiveCount > 0 && (
                            <span style={{
                                background: '#e67d21', color: '#fff',
                                borderRadius: 20, fontSize: 10, fontWeight: 700,
                                padding: '1px 5px', lineHeight: '14px',
                                minWidth: 16, textAlign: 'center',
                                pointerEvents: 'none',
                            }}>{todoActiveCount}</span>
                        )}
                    </button>

                    {/* Bildirimler çan ikonu */}
                    <div ref={bildirimPanelRef} style={{ position: 'relative' }}>
                        <button
                            title="Bildirimler"
                            onClick={handleBildirimIconClick}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#6b7280', fontSize: 17, padding: '4px 8px',
                                borderRadius: 8, lineHeight: 1, position: 'relative',
                                transition: 'color 0.15s',
                                display: 'flex', alignItems: 'center', gap: 5,
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
                        >
                            <i className="fas fa-bell"></i>
                            {okunmamisSayisi > 0 && (
                                <span style={{
                                    background: '#e53e3e', color: '#fff',
                                    borderRadius: 20, fontSize: 10, fontWeight: 700,
                                    padding: '1px 5px', lineHeight: '14px',
                                    minWidth: 16, textAlign: 'center',
                                    pointerEvents: 'none',
                                }}>{okunmamisSayisi > 99 ? '99+' : okunmamisSayisi}</span>
                            )}
                        </button>

                        {showBildirimPanel && (
                            <div style={{
                                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                width: 360, maxHeight: 480,
                                background: '#fff', borderRadius: 10,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                zIndex: 2000, display: 'flex', flexDirection: 'column',
                                overflow: 'hidden',
                            }}>
                                {/* Panel header */}
                                <div style={{
                                    padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    flexShrink: 0,
                                }}>
                                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1a2440' }}>
                                        {t('layout.bildirimler', 'Bildirimler')} {okunmamisSayisi > 0 && <span style={{ background: '#e53e3e', color: '#fff', borderRadius: 20, fontSize: 10, padding: '1px 6px', marginLeft: 4 }}>{okunmamisSayisi}</span>}
                                    </span>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {okunmamisSayisi > 0 && (
                                            <button onClick={handleTumunuOku} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 12, padding: '2px 6px' }}>
                                                {t('layout.tumunuOkunduSay', 'Tümünü okundu say')}
                                            </button>
                                        )}
                                        <Link to="/bildirimler" onClick={() => setShowBildirimPanel(false)} style={{ color: '#6b7280', fontSize: 12, textDecoration: 'none', padding: '2px 6px' }}>
                                            {t('layout.tumBildirimler', 'Tüm bildirimler')}
                                        </Link>
                                    </div>
                                </div>

                                {/* Bildirim listesi */}
                                <div style={{ overflowY: 'auto', flex: 1 }}>
                                    {bildirimler.length === 0 ? (
                                        <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af' }}>
                                            <i className="fas fa-bell-slash" style={{ fontSize: 28, marginBottom: 8, display: 'block' }}></i>
                                            {t('layout.bildirimYok', 'Bildirim yok')}
                                        </div>
                                    ) : (
                                        bildirimler.map(b => (
                                            <div
                                                key={b.bildirimID}
                                                style={{
                                                    padding: '10px 16px',
                                                    borderBottom: '1px solid #f5f5f5',
                                                    background: b.durum === 'Bekliyor' ? '#fffbf0' : '#fff',
                                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                                    cursor: b.url ? 'pointer' : 'default',
                                                    transition: 'background 0.1s',
                                                }}
                                                onClick={() => {
                                                    if (b.durum === 'Bekliyor') handleBildirimOku(b.bildirimID);
                                                    if (b.url) { setShowBildirimPanel(false); navigate(b.url); }
                                                }}
                                            >
                                                <span style={{
                                                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                                                    background: b.durum === 'Bekliyor' ? '#e53e3e' : '#d1d5db',
                                                }}></span>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: b.durum === 'Bekliyor' ? 700 : 400, fontSize: 13, color: '#1a2440', marginBottom: 2, wordBreak: 'break-word' }}>
                                                        {b.baslik}
                                                    </div>
                                                    {b.aciklama && (
                                                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3, wordBreak: 'break-word' }}>
                                                            {b.aciklama}
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                        {new Date(b.gonderilmeTarihi).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={e => { e.stopPropagation(); handleBildirimSil(b.bildirimID); }}
                                                    title="Kaldır"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 13, padding: '2px 4px', flexShrink: 0 }}
                                                    onMouseEnter={ev => (ev.currentTarget.style.color = '#e53e3e')}
                                                    onMouseLeave={ev => (ev.currentTarget.style.color = '#d1d5db')}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <span className="topbar-sep"></span>

                    {/* Profil Dropdown */}
                    <div className="profile-dropdown-wrap" style={{ position: 'relative' }}>
                        <button
                            className="topbar-user-btn"
                            onClick={() => setIsProfileOpen(v => !v)}
                            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
                        >
                            <span className="topbar-user-avatar">
                                {user.adSoyad ? user.adSoyad.charAt(0).toUpperCase() : 'U'}
                            </span>
                            {user.adSoyad}
                            <i className="fas fa-chevron-down" style={{ fontSize: '10px', marginLeft: '6px', color: '#9ca3af', transition: 'transform 0.2s', transform: isProfileOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}></i>
                        </button>

                        {isProfileOpen && (
                            <>
                                {/* backdrop to close */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                                    onClick={() => setIsProfileOpen(false)}
                                />
                                <div className="profile-dropdown-menu">
                                    {/* user info header */}
                                    <div className="profile-dropdown-header">
                                        <span className="profile-dropdown-avatar">
                                            {user.adSoyad ? user.adSoyad.charAt(0).toUpperCase() : 'U'}
                                        </span>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#151515' }}>{user.adSoyad}</div>
                                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>{user.email || 'Kullanıcı'}</div>
                                        </div>
                                    </div>
                                    <div className="profile-dropdown-divider" />
                                    <Link
                                        to="/profil"
                                        className="profile-dropdown-item"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <i className="fas fa-user-circle"></i>
                                        {t('header.profilim', 'Profilim')}
                                    </Link>
                                    {hasPermission('Ayarlar') && (
                                    <Link
                                        to="/ayarlar"
                                        className="profile-dropdown-item"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <i className="fas fa-sliders-h"></i>
                                        {t('header.ayarlar', 'Ayarlar')}
                                    </Link>
                                    )}
                                    {hasPermission('Ayarlar') && (
                                    <Link
                                        to="/destek-cms"
                                        className="profile-dropdown-item"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <i className="fas fa-book-open"></i>
                                        {t('header.destekCMS', 'Yardım İçerik Yönetimi')}
                                    </Link>
                                    )}
                                    <Link
                                        to="/destek"
                                        className="profile-dropdown-item"
                                        onClick={() => setIsProfileOpen(false)}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                    >
                                        <span>
                                            <i className="fas fa-life-ring"></i>
                                            {t('header.destekYardim', 'Destek & Yardım')}
                                        </span>
                                        {bildirimSayisi > 0 && (
                                            <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
                                                {bildirimSayisi}
                                            </span>
                                        )}
                                    </Link>
                                    <div className="profile-dropdown-divider" />
                                    <button
                                        className="profile-dropdown-item profile-dropdown-logout"
                                        onClick={handleLogout}
                                    >
                                        <i className="fas fa-sign-out-alt"></i>
                                        {t('header.cikisYap', 'Çıkış Yap')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* Mobil Döviz Kuru Modalı */}
            {showDovizModal && (
                <>
                    <div
                        className="doviz-modal-backdrop"
                        onClick={() => setShowDovizModal(false)}
                    />
                    <div className="doviz-modal">
                        <div className="doviz-modal-header">
                            <span className="doviz-modal-title">
                                <i className="fas fa-chart-line"></i> Döviz Kurları
                            </span>
                            <button className="doviz-modal-close" onClick={() => setShowDovizModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="doviz-modal-body">
                            {[
                                { kod: 'USD', isim: 'Amerikan Doları', val: currencies.usd },
                                { kod: 'EUR', isim: 'Euro', val: currencies.eur },
                            ].map(item => (
                                <div key={item.kod} className="doviz-modal-row">
                                    <div className="doviz-modal-flag">
                                        <span className="doviz-modal-kod">{item.kod}</span>
                                    </div>
                                    <div className="doviz-modal-info">
                                        <span className="doviz-modal-isim">{item.isim}</span>
                                    </div>
                                    <div className="doviz-modal-fiyat">
                                        <span className="doviz-modal-tl">₺{item.val}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="doviz-modal-footer">
                            Kurlar bilgi amaçlıdır.
                        </div>
                    </div>
                </>
            )}

            {/* Main Sidebar */}
            <aside className="main-sidebar sidebar-dark-primary elevation-4">
                <a href="/dashboard" className="brand-link brand-link-modern">
                    <div className="brand-logo-wrapper">
                        <div className="brand-logo-icon">
                            <img
                                src="/logo/yapıcrm_icon_transparan.png"
                                alt="logo"
                                className="brand-logo-img"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                    (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="brand-building"><div class="building-block b1"></div><div class="building-block b2"></div><div class="building-block b3"></div></div>';
                                }}
                            />
                        </div>
                        <div className="brand-logo-text">
                            <span className="brand-name">{firmaAdi}</span>
                            <span className="brand-tagline">{t('layout.yapiYonetimi', 'Yapı Yönetimi')}</span>
                        </div>
                    </div>
                </a>

                <div className="sidebar">
                    <nav className="mt-2">
                        <ul className="nav nav-pills nav-sidebar flex-column" role="menu">
                            <li className="nav-item">
                                <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-tachometer-alt"></i>
                                    <p>{t('menu.dashboard', 'Dashboard')}</p>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link to="/ajanda" className={`nav-link ${isActive('/ajanda') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-calendar-alt"></i>
                                    <p>{t('menu.ajanda', 'Ajanda')}</p>
                                </Link>
                            </li>
                            {hasPermission('MusteriBilgileri') && (
                            <li className="nav-item">
                                <Link to="/musteriler" className={`nav-link ${isActive('/musteriler') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-users"></i>
                                    <p>{t('menu.musteriler', 'Müşteriler')}</p>
                                </Link>
                            </li>
                            )}
                            {hasPermission('CariIslemleri') && (
                            <li className="nav-item">
                                <Link to="/cariler" className={`nav-link ${isActive('/cariler') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-user-tie"></i>
                                    <p>{t('menu.tedarikciler', 'Tedarikçiler')}</p>
                                </Link>
                            </li>
                            )}
                            {hasPermission('Projeler') && (
                            <li className={`nav-item ${isProjelerOpen ? 'menu-open' : ''}`}>
                                <a href="#"
                                   className={`nav-link ${location.pathname.startsWith('/daireler') || location.pathname.startsWith('/sozlesmeler') || location.pathname.startsWith('/satis-sozlesmesi') || location.pathname.startsWith('/musteri-sunumlari') || isActive('/ozet') || isActive('/proje-raporlari') ? 'active' : ''}`}
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setIsProjelerOpen(!isProjelerOpen);
                                   }}>
                                    <i className="nav-icon fas fa-home"></i>
                                    <p>
                                        {t('menu.projeler', 'Projeler')}
                                        <i className="fas fa-angle-left right"></i>
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={{ display: isProjelerOpen ? 'block' : 'none' }}>
                                    
                                    <li className="nav-item">
                                        <Link to="/daireler/liste" className={`nav-link ${location.pathname.startsWith('/daireler') ? 'active' : ''}`}>
                                            <i className="fas fa-th-large nav-icon"></i>
                                            <p>{t('menu.daireler', 'Daireler')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/sozlesmeler" className={`nav-link ${isActive('/sozlesmeler') || location.pathname.startsWith('/sozlesme-detay') ? 'active' : ''}`}>
                                            <i className="fas fa-file-contract nav-icon"></i>
                                            <p>{t('menu.sozlesmeler', 'Sözleşmeler')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/satis-sozlesmesi" className={`nav-link ${isActive('/satis-sozlesmesi') ? 'active' : ''}`}>
                                            <i className="fas fa-plus-circle nav-icon"></i>
                                            <p>{t('btn.yeniSozlesme', 'Yeni Sözleşme')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/musteri-sunumlari" className={`nav-link ${location.pathname.startsWith('/musteri-sunumlari') ? 'active' : ''}`}>
                                            <i className="fas fa-chalkboard-teacher nav-icon"></i>
                                            <p>{t('menu.musteriSunumlari', 'Müşteri Sunumları')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); openGorunumlerModal(); }}>
                                            <i className="fas fa-layer-group nav-icon"></i>
                                            <p>{t('menu.gorunumler', 'Görünümler')}</p>
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/ozet" className={`nav-link ${isActive('/ozet') ? 'active' : ''}`}>
                                            <i className="fas fa-chart-pie nav-icon"></i>
                                            <p>{t('menu.ozet', 'Özet')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/proje-raporlari" className={`nav-link ${isActive('/proje-raporlari') ? 'active' : ''}`}>
                                            <i className="fas fa-chart-line nav-icon"></i>
                                            <p>{t('menu.raporlar', 'Raporlar')}</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            )}
                            {hasPermission('Emlak') && (
                            <li className={`nav-item ${isEmlakOpen ? 'menu-open' : ''}`}>
                                <a href="#"
                                   className={`nav-link ${location.pathname.startsWith('/emlak-portfoy') || location.pathname.startsWith('/emlak-sunumlari') || location.pathname.startsWith('/emlak-rapor') ? 'active' : ''}`}
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setIsEmlakOpen(!isEmlakOpen);
                                   }}>
                                    <i className="nav-icon fas fa-city"></i>
                                    <p>
                                        {t('menu.emlak', 'Emlak')}
                                        <i className={`fas fa-angle-left right ${isEmlakOpen ? 'rotate-90' : ''}`}></i>
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={{ display: isEmlakOpen ? 'block' : 'none' }}>
                                    <li className="nav-item">
                                        <Link to="/emlak-portfoy" className={`nav-link ${isActive('/emlak-portfoy') || location.pathname.startsWith('/emlak-portfoy') ? 'active' : ''}`}>
                                            <i className="fas fa-building nav-icon"></i>
                                            <p>{t('menu.portfoy', 'Portföy')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/emlak-sunumlari" className={`nav-link ${isActive('/emlak-sunumlari') || location.pathname.startsWith('/emlak-sunumlari') ? 'active' : ''}`}>
                                            <i className="fas fa-tv nav-icon"></i>
                                            <p>{t('menu.sunumlar', 'Sunumlar')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/emlak-rapor" className={`nav-link ${isActive('/emlak-rapor') ? 'active' : ''}`}>
                                            <i className="fas fa-chart-bar nav-icon"></i>
                                            <p>{t('menu.rapor', 'Rapor')}</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            )}
                            {hasPermission('KiralikGayrimenkul') && (
                            <li className={`nav-item ${isKiralikOpen ? 'menu-open' : ''}`}>
                                <a href="#" 
                                   className={`nav-link ${isKiralikOpen ? 'active' : ''}`}
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setIsKiralikOpen(!isKiralikOpen);
                                   }}>
                                    <i className="nav-icon fas fa-key"></i>
                                    <p>
                                        {t('menu.kiralikGayrimenkuller', 'Kiralık Gayrimenkuller')}
                                        <i className={`fas fa-angle-left right ${isKiralikOpen ? 'rotate-90' : ''}`}></i>
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={{ display: isKiralikOpen ? 'block' : 'none' }}>
                                    <li className="nav-item">
                                        <Link to="/kiralik/portfoy" className={`nav-link ${isActive('/kiralik/portfoy') ? 'active' : ''}`}>
                                            <i className="fas fa-home nav-icon"></i>
                                            <p>{t('menu.portfoy', 'Portföy')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/kiralik/sozlesmeler" className={`nav-link ${isActive('/kiralik/sozlesmeler') ? 'active' : ''}`}>
                                            <i className="fas fa-file-signature nav-icon"></i>
                                            <p>{t('menu.sozlesmeler', 'Sözleşmeler')}</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            )}
                            {hasPermission('KatKarsiligArsa') && (
                            <li className="nav-item">
                                <Link to="/kat-karsiligi"
                                   className={`nav-link ${location.pathname.startsWith('/kat-karsiligi') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-handshake"></i>
                                    <p>{t('menu.katKarsiligi', 'Kat Karşılığı')}</p>
                                </Link>
                            </li>
                            )}
                            {hasPermission('FinansalIslemler') && (
                            <li className={`nav-item ${isFinansalOpen ? 'menu-open' : ''}`}>
                                <a href="#" 
                                   className={`nav-link ${isFinansalOpen ? 'active' : ''}`}
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setIsFinansalOpen(!isFinansalOpen);
                                   }}>
                                    <i className="nav-icon fas fa-coins"></i>
                                    <p>
                                        {t('menu.finansalIslemler', 'Finansal İşlemler')}
                                        <i className={`fas fa-angle-left right ${isFinansalOpen ? 'rotate-90' : ''}`}></i>
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={{ display: isFinansalOpen ? 'block' : 'none' }}>
                                    <li className="nav-item">
                                        <Link to="/finansal-islemler" className={`nav-link ${isActive('/finansal-islemler') ? 'active' : ''}`}>
                                            <i className="fas fa-exchange-alt nav-icon"></i>
                                            <p>{t('menu.islemler', 'İşlemler')}</p>
                                        </Link>
                                    </li>

                                    <li className="nav-item">
                                        <Link to="/finansal-ozet" className={`nav-link ${isActive('/finansal-ozet') ? 'active' : ''}`}>
                                            <i className="fas fa-chart-line nav-icon"></i>
                                            <p>{t('menu.finansalOzet', 'Finansal Özet')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/taksit-takip" className={`nav-link ${isActive('/taksit-takip') ? 'active' : ''}`}>
                                            <i className="fas fa-list-ol nav-icon"></i>
                                            <p>{t('menu.taksitTakip', 'Taksit Takip')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/raporlar" className={`nav-link ${isActive('/raporlar') ? 'active' : ''}`}>
                                            <i className="fas fa-file-invoice nav-icon"></i>
                                            <p>{t('menu.raporlar', 'Raporlar')}</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            )}
                            {hasPermission('PotansiyelMusteri') && (
                            <li className="nav-item">
                                <Link to="/lead-takip" className={`nav-link ${isActive('/lead-takip') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-headset"></i>
                                    <p>{t('menu.potansiyelMusteriTakip', 'Potansiyel Müşteri Takip')}</p>
                                </Link>
                            </li>
                            )}
                            {hasPermission('IsPlan') && (
                            <li className="nav-item">
                                <Link to="/is-plani" className={`nav-link ${isActive('/is-plani') ? 'active' : ''}`}>
                                    <i className="nav-icon fas fa-tasks"></i>
                                    <p>{t('menu.isPlan', 'İş Planı')}</p>
                                </Link>
                            </li>
                            )}
                            {hasPermission('Fihrist') && (
                            <li className={`nav-item ${isAraclarOpen ? 'menu-open' : ''}`}>
                                <a href="#" 
                                   className={`nav-link ${isAraclarOpen ? 'active' : ''}`}
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setIsAraclarOpen(!isAraclarOpen);
                                   }}>
                                    <i className="nav-icon fas fa-toolbox"></i>
                                    <p>
                                        {t('menu.araclar', 'Araçlar')}
                                        <i className={`fas fa-angle-left right ${isAraclarOpen ? 'rotate-90' : ''}`}></i>
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={{ display: isAraclarOpen ? 'block' : 'none' }}>
                                    <li className="nav-item">
                                        <Link to="/fihrist" className={`nav-link ${isActive('/fihrist') ? 'active' : ''}`}>
                                            <i className="fas fa-address-book nav-icon"></i>
                                            <p>{t('menu.fihrist', 'Fihrist')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/dokumanlar" className={`nav-link ${isActive('/dokumanlar') ? 'active' : ''}`}>
                                            <i className="fas fa-folder-open nav-icon"></i>
                                            <p>{t('menu.dokumanlar', 'Dokümanlar')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/kredi-hesaplama" className={`nav-link ${isActive('/kredi-hesaplama') ? 'active' : ''}`}>
                                            <i className="fas fa-calculator nav-icon"></i>
                                            <p>{t('menu.krediHesaplama', 'Kredi Hesaplama')}</p>
                                        </Link>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#" className="nav-link" onClick={e => { e.preventDefault(); setShowHesapMakinesi(true); }}>
                                            <i className="fas fa-square-root-alt nav-icon"></i>
                                            <p>{t('menu.hesapMakinesi', 'Hesap Makinesi')}</p>
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <Link to="/metin-editoru" className={`nav-link ${isActive('/metin-editoru') ? 'active' : ''}`}>
                                            <i className="fas fa-file-alt nav-icon"></i>
                                            <p>{t('menu.metinEditoru', 'Metin Editörü')}</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            )}

                        </ul>
                    </nav>
                </div>

                {/* Sidebar alt kullanıcı alanı */}
                <div className="sidebar-user-panel">
                    <div className="sidebar-user-info">
                        <span className="sidebar-user-avatar">
                            {user.adSoyad ? user.adSoyad.charAt(0).toUpperCase() : 'U'}
                        </span>
                        <div className="sidebar-user-text">
                            <div className="sidebar-user-name">{user.adSoyad}</div>
                            <div className="sidebar-user-email">{user.email || 'Kullanıcı'}</div>
                        </div>
                    </div>
                    <button className="sidebar-logout-btn" onClick={handleLogout} title="Çıkış Yap">
                        <i className="fas fa-sign-out-alt"></i>
                    </button>
                </div>
            </aside>

            {/* Mobil sidebar overlay */}
            {isMobileNavOpen && (
                <div
                    onClick={closeMobileSidebar}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 1049,
                    }}
                />
            )}

            {/* Content */}
            <div className="content-wrapper">
                <Outlet />
            </div>

            {/* Footer */}
            <footer className="main-footer">
                <span>Copyright &copy; 2025 {firmaAdi}. Tüm hakları saklıdır.</span>
                <span style={{ color: '#d1d5db' }}>v1.0.0</span>
            </footer>

            {/* ── To-Do / Takvim Paneli ── */}
            <TodoPanel
                open={showTodo}
                onClose={() => setShowTodo(false)}
                kullaniciID={user?.kullaniciID ?? 0}
                onCountChange={setTodoActiveCount}
            />

            {/* ── Görünümler — Proje Seçim Modalı ── */}
            {showGorunumlerModal && gorunumlerStep === 'proje' && (
                <>
                    <div
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2050 }}
                        onClick={() => setShowGorunumlerModal(false)}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '50%', left: '50%',
                        transform: 'translate(-50%,-50%)',
                        zIndex: 2051,
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
                        width: 520,
                        maxWidth: '95vw',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#1a2440' }}>{t('layout.gorunumlerProjeSecin', 'Görünümler — Proje Seçin')}</span>
                            <button
                                onClick={() => setShowGorunumlerModal(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 4 }}
                            >×</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                            {gorunumlerLoading ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}><i className="fas fa-spinner fa-spin"></i> {t('msg.yukleniyor', 'Yükleniyor…')}</div>
                            ) : gorunumlerProjeler.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>{t('layout.projeBulunamadi', 'Proje bulunamadı.')}</div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                    {gorunumlerProjeler.map((p: any) => (
                                        <button
                                            key={p.projeID}
                                            onClick={() => selectGorunumProje(p)}
                                            style={{
                                                background: '#f8fafc', border: '1px solid #e5e7eb',
                                                borderRadius: 10, padding: '16px 12px',
                                                cursor: 'pointer', textAlign: 'left',
                                                transition: 'all 0.15s',
                                                display: 'flex', flexDirection: 'column', gap: 4,
                                            }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#e8f0fe'; (e.currentTarget as HTMLElement).style.borderColor = '#3b5bdb'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc'; (e.currentTarget as HTMLElement).style.borderColor = '#e5e7eb'; }}
                                        >
                                            <span style={{ fontWeight: 600, fontSize: 14, color: '#1a2440' }}>{p.projeAdi}</span>
                                            {p.projeKodu && <span style={{ fontSize: 11, color: '#9ca3af' }}>{p.projeKodu}</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ── Görünümler — ProjeGorunumleri (kendi overlay'i ile) ── */}
            {showGorunumlerModal && gorunumlerStep === 'gorunum' && (
                gorunumlerLoading ? (
                    <>
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998 }} />
                        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, color: '#fff', fontSize: 18 }}>
                            <i className="fas fa-spinner fa-spin"></i> {t('msg.yukleniyor', 'Yükleniyor…')}
                        </div>
                    </>
                ) : selectedGorunumProje && (
                    <ProjeGorunumleri
                        projeID={selectedGorunumProje.projeID}
                        daireler={gorunumlerDaireler}
                        bloklar={gorunumlerBloklar.map((b: any) => ({ id: b.blokID, ad: b.blokAdi }))}
                        katlar={gorunumlerKatlar.map((k: any) => ({ id: k.katID, ad: k.katAdi }))}
                        cepheler={gorunumlerCepheler.map((c: any) => ({ id: c.cepheID, ad: c.cepheAdi }))}
                        manzaralar={gorunumlerManzaralar.map((m: any) => ({ id: m.manzaraID, ad: m.manzaraAdi }))}
                        onClose={() => setGorunumlerStep('proje')}
                        readOnly={true}
                    />
                )
            )}

            {/* ── Hesap Makinesi Modal ── */}
            {showHesapMakinesi && (
                <>
                    <div style={{
                        position: 'fixed',
                        ...(calcPos
                            ? { left: calcPos.x, top: calcPos.y, transform: 'none' }
                            : { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }
                        ),
                        zIndex: 2001,
                        width: 300,
                        background: '#1a2440',
                        borderRadius: 20,
                        boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
                        overflow: 'hidden',
                        fontFamily: "'Inter', sans-serif",
                        userSelect: 'none',
                    }}>
                        {/* Başlık — sürükleme tutacağı */}
                        <div
                            onMouseDown={e => {
                                e.preventDefault();
                                isDragging.current = true;
                                const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
                                dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                            }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 10px', cursor: 'grab' }}
                        >
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, letterSpacing: '0.04em' }}>HESAP MAKİNESİ</span>
                            <button onClick={() => setShowHesapMakinesi(false)} onMouseDown={e => e.stopPropagation()} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 4, borderRadius: 6 }}>×</button>
                        </div>
                        {/* Ekran */}
                        <div style={{ padding: '0 18px 14px' }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', minHeight: 18, textAlign: 'right', marginBottom: 2 }}>
                                {calcPrev && calcOp ? `${calcPrev} ${calcOp}` : '\u00a0'}
                            </div>
                            <div style={{
                                fontSize: calcDisplay.length > 12 ? 22 : 36,
                                fontWeight: 300,
                                color: '#fff',
                                textAlign: 'right',
                                letterSpacing: '-1px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>{calcDisplay}</div>
                        </div>
                        {/* Butonlar */}
                        <CalcButtons
                            display={calcDisplay}
                            prev={calcPrev}
                            op={calcOp}
                            newNum={calcNewNum}
                            setDisplay={setCalcDisplay}
                            setPrev={setCalcPrev}
                            setOp={setCalcOp}
                            setNewNum={setCalcNewNum}
                            onClose={() => setShowHesapMakinesi(false)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

/* ── Hesap Makinesi Buton Bileşeni ── */
interface CalcProps {
    display: string; prev: string | null; op: string | null; newNum: boolean;
    setDisplay: (v: string) => void; setPrev: (v: string | null) => void;
    setOp: (v: string | null) => void; setNewNum: (v: boolean) => void;
    onClose: () => void;
}
const CalcButtons: React.FC<CalcProps> = ({ display, prev, op, newNum, setDisplay, setPrev, setOp, setNewNum, onClose }) => {
    const fmtResult = (n: number) => {
        if (!isFinite(n)) return 'Hata';
        const s = parseFloat(n.toPrecision(12)).toString();
        return s;
    };
    const pressNum = (d: string) => {
        if (newNum) { setDisplay(d === '.' ? '0.' : d); setNewNum(false); }
        else {
            if (d === '.' && display.includes('.')) return;
            setDisplay(display === '0' && d !== '.' ? d : display + d);
        }
    };
    const pressOp = (o: string) => {
        setPrev(display); setOp(o); setNewNum(true);
    };
    const pressEq = () => {
        if (!prev || !op) { setNewNum(true); return; }
        const a = parseFloat(prev), b = parseFloat(display);
        let r = 0;
        if (op === '+') r = a + b;
        else if (op === '-') r = a - b;
        else if (op === '×') r = a * b;
        else if (op === '÷') r = b !== 0 ? a / b : NaN;
        const res = fmtResult(r);
        setDisplay(res); setPrev(null); setOp(null); setNewNum(true);
    };
    const pressClear = () => { setDisplay('0'); setPrev(null); setOp(null); setNewNum(true); };
    const pressCE = () => { setDisplay('0'); setNewNum(true); };
    const pressSign = () => { setDisplay(display.startsWith('-') ? display.slice(1) : '-' + display); };
    const pressPct = () => { setDisplay(fmtResult(parseFloat(display) / 100)); };
    const pressBack = () => {
        if (newNum) return;
        const s = display.slice(0, -1);
        setDisplay(s === '' || s === '-' ? '0' : s);
    };

    // Klavye desteği
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key >= '0' && e.key <= '9') { e.preventDefault(); pressNum(e.key); }
            else if (e.key === '.') { e.preventDefault(); pressNum('.'); }
            else if (e.key === '+') { e.preventDefault(); pressOp('+'); }
            else if (e.key === '-') { e.preventDefault(); pressOp('-'); }
            else if (e.key === '*') { e.preventDefault(); pressOp('×'); }
            else if (e.key === '/') { e.preventDefault(); pressOp('÷'); }
            else if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); pressEq(); }
            else if (e.key === 'Backspace') { e.preventDefault(); pressBack(); }
            else if (e.key === 'Delete') { e.preventDefault(); pressClear(); }
            else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            else if (e.key === '%') { e.preventDefault(); pressPct(); }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [display, prev, op, newNum]);

    type BtnDef = { label: string; action: () => void; color?: string; textColor?: string; flex?: number };
    const rows: BtnDef[][] = [
        [
            { label: 'AC', action: pressClear, color: '#2d3a5a', textColor: '#e67d21' },
            { label: '±', action: pressSign, color: '#2d3a5a', textColor: '#e0e4ef' },
            { label: '%', action: pressPct, color: '#2d3a5a', textColor: '#e0e4ef' },
            { label: '÷', action: () => pressOp('÷'), color: '#e67d21', textColor: '#fff' },
        ],
        [
            { label: '7', action: () => pressNum('7'), color: '#243060' },
            { label: '8', action: () => pressNum('8'), color: '#243060' },
            { label: '9', action: () => pressNum('9'), color: '#243060' },
            { label: '×', action: () => pressOp('×'), color: '#e67d21', textColor: '#fff' },
        ],
        [
            { label: '4', action: () => pressNum('4'), color: '#243060' },
            { label: '5', action: () => pressNum('5'), color: '#243060' },
            { label: '6', action: () => pressNum('6'), color: '#243060' },
            { label: '-', action: () => pressOp('-'), color: '#e67d21', textColor: '#fff' },
        ],
        [
            { label: '1', action: () => pressNum('1'), color: '#243060' },
            { label: '2', action: () => pressNum('2'), color: '#243060' },
            { label: '3', action: () => pressNum('3'), color: '#243060' },
            { label: '+', action: () => pressOp('+'), color: '#e67d21', textColor: '#fff' },
        ],
        [
            { label: '⌫', action: pressBack, color: '#2d3a5a', textColor: '#e0e4ef' },
            { label: '0', action: () => pressNum('0'), color: '#243060' },
            { label: '.', action: () => pressNum('.'), color: '#243060' },
            { label: '=', action: pressEq, color: '#e67d21', textColor: '#fff' },
        ],
    ];

    return (
        <div style={{ padding: '0 10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 8 }}>
                    {row.map((btn, bi) => (
                        <button
                            key={bi}
                            onClick={btn.action}
                            style={{
                                flex: btn.flex ?? 1,
                                height: 58,
                                borderRadius: 12,
                                border: 'none',
                                background: btn.color ?? '#243060',
                                color: btn.textColor ?? '#e0e4ef',
                                fontSize: 20,
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'filter 0.12s',
                                fontFamily: "'Inter', sans-serif",
                            }}
                            onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.18)')}
                            onMouseLeave={e => (e.currentTarget.style.filter = 'brightness(1)')}
                        >
                            {btn.label}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
};
export default DashboardLayout;
